import type { BlocksContent } from "@strapi/blocks-react-renderer";

import { parse } from "date-fns";

import type { ImageMedia } from "@/src/domain/model/ImageMedia";

import { logger } from "@/src/infrastructure/datadog/Datadog";

export type StrapiContent<T> = { documentId: number } & T;

export interface StrapiMedia {
  alternativeText?: null | string | undefined;
  caption?: null | string | undefined;

  // Possible heights: large (1000px), medium (750px), small (500px), thumbnail (156px)
  formats?: {
    large?: { height: number; url: string; width: number };
  };

  height: number;
  name: string;
  url: string;
  width: number;
}

export interface StrapiPaginatedResponse<T> {
  data: StrapiContent<T>[];
  meta: {
    pagination: {
      page: number;
      pageCount: number;
      pageSize: number;
      total: number;
    };
  };
}

// Relations can easily be missing on Strapi
export type StrapiRelation<T> = null | StrapiContent<T> | undefined;
export type StrapiRelations<T> = null | StrapiContent<T>[] | undefined;

export interface StrapiResponse<T> {
  data: T;
}

export function mapToBlocksContent(
  blocksContent: BlocksContent | null,
): BlocksContent {
  // Filter out empty blocks, e.g. [{ children: [{ text: "", type: "text" }], type: "paragraph" }]
  return (blocksContent ?? []).filter(
    (block) =>
      block.type !== "paragraph" ||
      !block.children.every(
        (child) => child.type === "text" && child.text.trim() === "",
      ),
  );
}

// Add the UTC (Z) timezone to make sure that the created Date is in UTC (not the machine timezone)
export function mapToDate(stringDate: string): Date {
  return parse(`${stringDate}Z`, "yyyy-MM-ddXXX", new Date());
}

export function mapToImage(
  media: null | StrapiContent<StrapiMedia> | undefined,
  missingImageBaseUrl: string = process.env.NEXT_PUBLIC_BASE_URL,
): ImageMedia {
  if (!media) {
    return {
      alt: null,
      caption: null,
      height: 100,
      id: Number.MAX_SAFE_INTEGER,
      url: `${missingImageBaseUrl}/missing-image.webp`,
      width: 100,
    };
  }

  const alt = (media.alternativeText ?? "") || null;
  if (!alt) {
    logger.warn(`Alternative text is missing for image: ${media.name}`);
  }

  const { height, url, width } = media.formats?.large ?? media;
  return {
    alt,
    caption: (media.caption ?? "") || null,
    height,
    id: media.documentId,
    url: mediaUrl(url),
    width,
  };
}

// Media URL are sometimes absolute and sometimes relative
export function mediaUrl(
  url: string,
  mediaUrlPrefix = process.env.NEXT_PUBLIC_STRAPI_MEDIA_URL,
): string {
  return url.includes("://") ? url : `${mediaUrlPrefix}${url}`;
}

// https://docs.strapi.io/dev-docs/api/rest/populate-select
export function queryParams(
  populate: string[],
  otherParams: [string, string][] = [],
): URLSearchParams {
  return new URLSearchParams([
    ...populate.map((p, index) => [`populate[${index}]`, p]),
    ...otherParams,
  ]);
}
